const STORAGE_KEY = "infermesh.apiKey";

export function getApiKey(): string {
  return localStorage.getItem(STORAGE_KEY) ?? "";
}

export function setApiKey(key: string): void {
  if (key) localStorage.setItem(STORAGE_KEY, key);
  else localStorage.removeItem(STORAGE_KEY);
}

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, message: string, code = "error") {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export async function api<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const key = getApiKey();
  const headers: Record<string, string> = {
    "content-type": "application/json",
    ...(options.headers as Record<string, string> | undefined),
  };
  if (key) headers.authorization = `Bearer ${key}`;

  const res = await fetch(path, {
    ...options,
    credentials: "include",
    headers,
  });
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { error: { message: text || res.statusText } };
  }

  if (!res.ok) {
    const err = (data as { error?: { message?: string; code?: string } })?.error;
    throw new ApiError(
      res.status,
      err?.message ?? res.statusText,
      err?.code ?? "error",
    );
  }
  return data as T;
}

export interface ConsoleUserDto {
  id: string;
  username: string;
  displayName: string;
  role: string;
  status: "active" | "disabled";
  email?: string;
  lastLoginAt: string | null;
  createdAt?: string;
  capabilities: string[];
}

export interface ConsoleRoleDto {
  role: string;
  description: string;
}

export interface ConsoleAuthState {
  authenticated: boolean;
  /** True when the instance has console credentials configured. */
  signInEnabled: boolean;
  user: ConsoleUserDto | null;
  environment: string;
  sessionTtlHours: number;
  minPasswordLength: number;
  roles: ConsoleRoleDto[];
}

export interface ConsoleSessionDto {
  id: string;
  userId: string;
  username: string | null;
  createdAt: string;
  expiresAt: string;
  lastSeenAt: string | null;
  ip: string | null;
  userAgent: string | null;
  current: boolean;
}

/** Capability check mirroring the server-side role table. */
export function hasCapability(
  auth: ConsoleAuthState | null,
  capability: "read" | "write" | "manage_credentials" | "manage_users",
): boolean {
  return Boolean(auth?.user?.capabilities?.includes(capability));
}

export async function authMe(): Promise<ConsoleAuthState> {
  return api<ConsoleAuthState>("/api/auth/me");
}

export async function signIn(username: string, password: string): Promise<void> {
  await api("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}

export async function signOut(): Promise<void> {
  try {
    await api("/api/auth/logout", { method: "POST" });
  } finally {
    // A stale API key must not keep the console "signed in" after logout.
    setApiKey("");
  }
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<{ otherSessionsRevoked: number }> {
  return api<{ otherSessionsRevoked: number }>("/api/auth/password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function listUsers(): Promise<{ data: ConsoleUserDto[]; roles: Array<ConsoleRoleDto & { capabilities: string[] }> }> {
  return api("/api/users");
}

export async function createUser(input: {
  username: string;
  displayName?: string;
  password: string;
  role: string;
}): Promise<{ user: ConsoleUserDto }> {
  return api("/api/users", { method: "POST", body: JSON.stringify(input) });
}

export async function updateUser(
  id: string,
  patch: { displayName?: string; role?: string; status?: "active" | "disabled" },
): Promise<{ user: ConsoleUserDto }> {
  return api(`/api/users/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
}

export async function resetUserPassword(id: string, password: string): Promise<{ sessionsRevoked: number }> {
  return api(`/api/users/${id}/password`, { method: "POST", body: JSON.stringify({ password }) });
}

export async function disableUser(id: string): Promise<{ user: ConsoleUserDto; sessionsRevoked: number }> {
  return api(`/api/users/${id}`, { method: "DELETE" });
}

export async function listSessions(all = false): Promise<{ current: string | null; scope: string; data: ConsoleSessionDto[] }> {
  return api(`/api/sessions${all ? "?all=1" : ""}`);
}

export async function revokeSession(id: string): Promise<{ revoked: boolean }> {
  return api(`/api/sessions/${id}`, { method: "DELETE" });
}

export interface OverviewResponse {
  window: string;
  metrics: {
    requests: number;
    successRate: number;
    avgLatencyMs: number;
    avgTtftMs: number;
    totalCostUsd: number;
    freeRequestCount: number;
    freeModelCount: number;
    fallbacks: number;
    cacheHits: number;
  };
  providers: Array<{
    id: string;
    providerTypeId: string;
    displayName: string;
    status: string;
    priority: number;
    health: string;
    successRate: number | null;
    p50LatencyMs: number | null;
    modelCount: number;
    freeModelCount: number;
  }>;
  quotas: Array<{
    providerId: string;
    modelId: string | null;
    limits: Array<{
      window: string;
      kind: string;
      limit: number;
      used: number;
      remaining: number;
      source: string;
    }>;
    capturedAt: string;
  }>;
  circuits: Array<{ key: string; state: string; failures: number; label?: string }>;
  recentRequests: Array<RequestRow>;
  modelUsage: Array<Record<string, unknown>>;
}

export interface RequestRow {
  id: string;
  traceId: string;
  endpoint: string;
  requestedModel: string;
  resolvedProviderId: string | null;
  resolvedModelId: string | null;
  status: string;
  streaming: boolean;
  cacheHit: boolean;
  latencyMs: number | null;
  ttftMs: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  costUsd: number | null;
  freeTier: boolean;
  fallbackCount: number;
  retryCount: number;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
  routingExplanation?: {
    selected?: {
      displayName: string;
      providerDisplayName: string;
      reasons: string[];
    } | null;
    rejected?: Array<{
      displayName: string;
      providerDisplayName: string;
      rejectionReason?: string;
    }>;
  } | null;
}

export interface RequestAttempt {
  attemptNumber: number;
  providerId: string;
  providerTypeId: string;
  modelId: string;
  providerModelId: string;
  status: string;
  httpStatus: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  latencyMs: number;
  ttftMs: number | null;
  freeTier: boolean;
  startedAt: string;
  finishedAt: string | null;
}

export interface RequestDetail extends RequestRow {
  attempts: RequestAttempt[];
}

export interface ProviderDefinitionDto {
  id: string;
  slug: string;
  name: string;
  kind: "managed" | "custom" | "demo";
  category?: string;
  lifecycle?: string;
  shortDescription: string;
  description: string;
  websiteUrl: string;
  docsUrl: string;
  apiKeyUrl: string;
  setupInstructions: {
    title: string;
    description: string;
    steps: string[];
    notes?: string[];
  };
  defaultBaseUrl: string;
  protocol: string;
  capabilities: string[];
  freeTier: { available: boolean | "unknown"; notes?: string };
  freeAccess?: {
    types: string[];
    keyRequired: "required" | "optional" | "none" | "conditional";
    notes?: string;
    quotaSemantics?: string;
    lastVerifiedAt?: string;
    verificationSource?: string;
    verificationConfidence?: string;
  };
  branding: { letter: string; accent: string };
  features: string[];
  credentialFields: Array<{ key: string; label: string; kind: string; required: boolean; placeholder?: string; helpText?: string }>;
}

export interface ProviderVerificationDto {
  ok: boolean;
  steps: Array<{
    id: string;
    label: string;
    ok: boolean;
    detail?: string;
    durationMs?: number;
  }>;
  modelsDiscovered?: number;
  freeModelCount?: number;
  capabilities?: string[];
  error?: { message: string; action?: string; code?: string };
  verifiedAt: string;
}

export interface ProviderView {
  id: string;
  providerTypeId: string;
  name: string;
  kind: "managed" | "custom" | "demo";
  shortDescription: string;
  description: string;
  connectionStatus:
    | "not_connected"
    | "connecting"
    | "connected"
    | "verifying"
    | "healthy"
    | "degraded"
    | "unhealthy"
    | "quota_exhausted"
    | "disabled"
    | "error";
  status: string;
  connectionMode?: "anonymous" | "authenticated" | "byok" | "hybrid" | "none";
  credentials?: {
    state: "none_required" | "anonymous" | "configured" | "missing";
    detail: string;
    verifiedKeyLabels: string[];
  };
  keyCount: number;
  keyLabels: Array<{ id: string; label: string; prefix: string; isPrimary: boolean; status: string }>;
  /** Routing priority (lower = preferred by NEXUS). */
  priority?: number;
  modelCount: number;
  freeModelCount: number;
  health: { state: string; successRate: number | null; p50LatencyMs: number | null } | null;
  quota: {
    source: string;
    remainingRatio: number | null;
    note?: string | null;
    limits: Array<{ window: string; kind: string; remaining: number; limit: number; source: string }>;
  } | null;
  lastVerifiedAt: string | null;
  verification: ProviderVerificationDto | null;
  definition: {
    apiKeyUrl: string;
    docsUrl: string;
    websiteUrl: string;
    capabilities: string[];
    freeTier: { available: boolean | "unknown"; notes?: string };
    freeAccess?: ProviderDefinitionDto["freeAccess"];
    category?: string;
    lifecycle?: string;
    keyRequired?: string;
    features: string[];
    branding: { letter: string; accent: string };
    setupInstructions: ProviderDefinitionDto["setupInstructions"];
    defaultBaseUrl: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ProviderCatalogResponse {
  managed: ProviderDefinitionDto[];
  anonymous?: ProviderDefinitionDto[];
  demo: ProviderDefinitionDto[];
  retired?: ProviderDefinitionDto[];
  custom: ProviderDefinitionDto;
}

export interface ProviderCard extends ProviderView {}

export interface ModelRow {
  id: string;
  logicalId: string;
  providerModelId: string;
  displayName: string;
  contextWindow: number;
  capabilities: Record<string, boolean>;
  pricing: { tier: string; inputPerMillion: number; outputPerMillion: number };
  scores: Record<string, number>;
  enabled: boolean;
  provider?: { providerTypeId: string; displayName: string; status: string } | null;
}

export interface RoutingDecisionResponse {
  requestId: string;
  providerId: string;
  providerDisplayName?: string;
  modelId: string;
  providerModelId: string;
  displayName: string;
  fallbackChain?: Array<{
    providerId: string;
    modelId: string;
    displayName: string;
    role: string;
  }>;
  explanation: {
    selected: {
      displayName: string;
      providerDisplayName: string;
      reasons: string[];
      scoreBreakdown: Record<string, number>;
    } | null;
    intent: string;
    taskCategory: string;
    profile: string;
    requiredCapabilities: string[];
    accepted: Array<{
      displayName: string;
      providerDisplayName: string;
      providerModelId: string;
      pricing: { tier: string };
      scoreBreakdown: { total: number } | null;
    }>;
    rejected: Array<{
      displayName: string;
      providerDisplayName: string;
      providerModelId: string;
      rejectionReason?: string;
      rejectionDetail?: string;
    }>;
    fallbackChain: Array<{
      providerId: string;
      modelId: string;
      displayName: string;
      role: string;
    }>;
  };
}

export function formatMs(ms: number | null | undefined): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function formatPct(n: number | null | undefined): string {
  if (n == null) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

export function formatUsd(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n === 0) return "$0";
  if (n < 0.01) return `$${n.toFixed(6)}`;
  return `$${n.toFixed(4)}`;
}

export function healthChip(state: string | null | undefined): string {
  switch (state) {
    case "healthy":
      return "chip-ok";
    case "degraded":
      return "chip-warn";
    case "unhealthy":
    case "disabled":
      return "chip-danger";
    default:
      return "chip-muted";
  }
}

export function statusChip(status: string): string {
  if (status === "success" || status === "active") return "chip-ok";
  if (status === "error" || status === "disabled") return "chip-danger";
  return "chip-muted";
}
